import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  DatePickerV2,
  labraUIDateFormat,
  NoteV2,
  PlanType,
  TextArea,
  TextInputV2,
} from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { debounce, get, isEmpty } from 'lodash'
import { useCallback } from 'react'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { useStyles } from '../AddEditChannelOfferComponents.styles'
import {
  channelOfferExpirationDateField,
  channelOfferSubscriptionEndDateField,
} from '../../../../utils/channelOfferUtil'
import { OfferRecipientV2 } from '../../../../common/components/OfferRecipientV2/OfferRecipientV2'

export const ChannelOfferDetail = ({
  productType,
  buyerAwsAccountId,
  isAbo,
  noteDescription,
}: {
  productType?: PlanType
  buyerAwsAccountId?: string
  isAbo?: boolean
  noteDescription?: string
}) => {
  const { classes } = useStyles()
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { touched, errors, values, handleBlur, setFieldValue } = formikProps

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    []
  )

  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )

  const getErrorMsg = (keyName: string) => {
    return (!!get(touched, keyName, false) && get(errors, keyName, ' ')) || ''
  }

  return (
    <div data-testid="details-section">
      <BoxV2
        heading={'Offer details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {noteDescription && <NoteV2 description={noteDescription} />}
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            <div className={classes.inputWidth}>
              <TextInputV2
                keyName="companyName"
                title="Buyer name"
                type="text"
                placeHolder=""
                value={values.companyName as string}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'companyName', false) &&
                    get(errors, 'companyName', false)
                  )
                }
                onChange={formChangeHandler('companyName')}
                isReadOnly={!!isAbo}
                isRequired={false}
                errorText={getErrorMsg('companyName')}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
            <div className={classes.inputWidth}>
              <TextInputV2
                keyName="awsAccountId"
                title="AWS account ID"
                type="text"
                placeHolder=""
                value={(values.awsAccountId || '') as string}
                maxNumber={12}
                isError={
                  !!(
                    get(touched, 'awsAccountId', false) &&
                    get(errors, 'awsAccountId', false)
                  )
                }
                onChange={formChangeHandler('awsAccountId')}
                isReadOnly={isAbo || !!buyerAwsAccountId}
                isRequired={false}
                errorText={getErrorMsg('awsAccountId')}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          </div>

          <div className={classes.companyDetailsContainer}>
            <div className={classes.inputWidth}>
              <TextInputV2
                keyName="offerName"
                title="Offer name"
                type="text"
                placeHolder=""
                value={values.offerName as string}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'offerName', false) &&
                    get(errors, 'offerName', false)
                  )
                }
                onChange={formChangeHandler('offerName')}
                isReadOnly={false}
                isRequired={false}
                errorText={getErrorMsg('offerName')}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
            <div className={classes.inputWidth}>
              <DatePickerV2
                format={labraUIDateFormat}
                field={channelOfferExpirationDateField}
                key={'offerExpirationDate'}
                views={['year', 'month', 'day']}
                showErrorHelper={
                  !!touched.offerExpirationDate &&
                  !isEmpty(errors.offerExpirationDate)
                }
                fieldId={'offerExpirationDate'}
                formik={
                  formikProps as unknown as FormikProps<Record<string, unknown>>
                }
                errorHelperText={
                  (!!touched.offerExpirationDate &&
                    errors.offerExpirationDate) ||
                  ''
                }
              />
            </div>
          </div>
          <div className={classes.inputFullWidth}>
            <TextInputV2
              keyName="offerDescription"
              title="Offer description (Optional)"
              type="text"
              placeHolder=""
              value={values.offerDescription as string}
              maxNumber={72}
              isError={
                !!(
                  get(touched, 'offerDescription', false) &&
                  get(errors, 'offerDescription', false)
                )
              }
              onChange={formChangeHandler('offerDescription')}
              isReadOnly={false}
              isRequired={false}
              errorText={getErrorMsg('offerDescription')}
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
          {productType === PlanType.SAAS_SUBSCRIPTION && (
            <div className={classes.inputWidth}>
              <DatePickerV2
                format={labraUIDateFormat}
                field={channelOfferSubscriptionEndDateField}
                key={'subscriptionEndDate'}
                views={['year', 'month', 'day']}
                showErrorHelper={
                  !!touched.subscriptionEndDate &&
                  !isEmpty(errors.subscriptionEndDate)
                }
                fieldId={'subscriptionEndDate'}
                formik={
                  formikProps as unknown as FormikProps<Record<string, unknown>>
                }
                errorHelperText={
                  (!!touched.subscriptionEndDate &&
                    errors.subscriptionEndDate) ||
                  ''
                }
              />
            </div>
          )}
        </div>
        <div
          className={classes.inputFullWidth}
          data-testid="channel-offer-recipients-details"
        >
          <p className={classes.sectionHeading}>Offer recipients</p>
          <OfferRecipientV2
            formik={
              formikProps as unknown as FormikProps<Record<string, unknown>>
            }
            smallSize={true}
          />
        </div>
        <div
          className={classes.inputFullWidth}
          data-testid="channel-offer-email-custom-message"
        >
          <TextArea
            keyName="customEmailMessage"
            title="Add custom email message to be sent to recipients (optional)"
            rows={10}
            maxNumber={1000}
            height={100}
            type="string"
            placeHolder=""
            value={values.customEmailMessage as string}
            isError={
              !!(errors.customEmailMessage && touched.customEmailMessage)
            }
            onChange={formChangeHandler('customEmailMessage')}
            isReadOnly={false}
            isRequired={false}
            errorText={
              (!!touched.customEmailMessage && errors.customEmailMessage) || ''
            }
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>
      </BoxV2>
    </div>
  )
}
